import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Envelope, PaperPlaneTilt, Question, ChatCircle } from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, formData);
      toast({
        title: 'Message Sent!',
        description: "We'll get back to you within 24 hours.",
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast({
        title: 'Could not send message',
        description: err.response?.data?.detail || 'Please try again or email support@constructhelp.com directly.',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]">
      {/* Hero */}
      <div className="bg-[#0F172A] border-b-4 border-black py-12">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-[#2B4C6F] border-2 border-white flex items-center justify-center">
              <ChatCircle size={32} weight="bold" className="text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-black text-white uppercase tracking-tight">Contact Us</h1>
              <p className="text-neutral-400">We're here to help. Reach out anytime!</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Form */}
          <div className="bg-white dark:bg-[#0F172A] border-4 border-black shadow-[8px_8px_0px_#2B4C6F] p-6">
            <h2 className="text-2xl font-black uppercase mb-6 flex items-center gap-2">
              <PaperPlaneTilt size={28} weight="bold" className="text-[#2B4C6F]" />
              Send a Message
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="name" className="font-bold text-sm uppercase tracking-wider">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="mt-2 h-12 border-2 border-black rounded-none focus:ring-2 focus:ring-[#2B4C6F]"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <Label htmlFor="email" className="font-bold text-sm uppercase tracking-wider">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  className="mt-2 h-12 border-2 border-black rounded-none focus:ring-2 focus:ring-[#2B4C6F]"
                  data-testid="contact-email"
                />
              </div>
              <div>
                <Label htmlFor="subject" className="font-bold text-sm uppercase tracking-wider">Subject *</Label>
                <Input
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="What is this about?"
                  className="mt-2 h-12 border-2 border-black rounded-none focus:ring-2 focus:ring-[#2B4C6F]"
                  data-testid="contact-subject"
                />
              </div>
              <div>
                <Label htmlFor="message" className="font-bold text-sm uppercase tracking-wider">Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us more..."
                  rows={5}
                  className="mt-2 border-2 border-black rounded-none focus:ring-2 focus:ring-[#2B4C6F] resize-none"
                  data-testid="contact-message"
                />
              </div>
              <Button 
                type="submit" 
                disabled={sending}
                className="w-full h-14 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#000] rounded-none"
                data-testid="contact-submit"
              >
                {sending ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <PaperPlaneTilt size={22} weight="bold" className="mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-[#0F172A] border-4 border-black shadow-[8px_8px_0px_#D95800] p-6">
              <h2 className="text-2xl font-black uppercase mb-4">Get in Touch</h2>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                Have questions? We'd love to hear from you. Our team is here to help you succeed on ConstructHelp.
              </p>

              <div className="flex items-start gap-4 p-4 bg-neutral-100 dark:bg-neutral-800 border-2 border-black">
                <div className="w-12 h-12 bg-[#2B4C6F] border-2 border-black flex items-center justify-center flex-shrink-0">
                  <Envelope size={24} weight="bold" className="text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Email</h3>
                  <a href="mailto:support@constructhelp.com" className="text-[#2B4C6F] hover:underline font-medium">
                    support@constructhelp.com
                  </a>
                  <p className="text-sm text-neutral-500 mt-1">We'll respond within 24 hours</p>
                </div>
              </div>
            </div>

            <div className="bg-[#2B4C6F] border-4 border-black shadow-[8px_8px_0px_#000] p-6">
              <div className="flex items-center gap-3 mb-3">
                <Question size={28} weight="bold" className="text-white" />
                <h3 className="font-black text-white text-xl uppercase">Need Quick Answers?</h3>
              </div>
              <p className="text-white/80 mb-4">
                Check out our Help Center for answers to common questions about using ConstructHelp.
              </p>
              <Button 
                variant="outline" 
                className="w-full h-12 bg-white text-[#2B4C6F] font-bold uppercase border-2 border-black shadow-[3px_3px_0px_#000] rounded-none hover:bg-neutral-100"
              >
                Visit Help Center
              </Button>
            </div>

            {/* Response Time */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border-4 border-black p-4">
              <p className="font-bold text-amber-800 dark:text-amber-300 text-sm uppercase tracking-wider">
                Average Response Time
              </p>
              <p className="text-2xl font-black text-amber-600 dark:text-amber-400">Under 24 Hours</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
